import React, { useState, useEffect } from 'react';
import { usuariosAPI, talleresAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, User, Building2, Loader2, Eye, EyeOff } from 'lucide-react';
import { formatApiError } from '../../utils/apiError';

// Helper para ordenar talleres por número ANAC
const sortTalleresByAnac = (talleres) => {
  return [...talleres].sort((a, b) => {
    const anacA = a.numero_anac || '';
    const anacB = b.numero_anac || '';
    return anacA.localeCompare(anacB, undefined, { numeric: true, sensitivity: 'base' });
  });
};

const AdminUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [talleres, setTalleres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTaller, setFilterTaller] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'taller_user',  // Rol del sistema
    cargo: 'tecnico_aeronautico',  // Cargo en la empresa
    taller_id: ''
  });

  useEffect(() => {
    loadData();
  }, [filterTaller]);

  const loadData = async () => {
    try {
      const [usuariosRes, talleresRes] = await Promise.all([
        usuariosAPI.getAll(filterTaller ? { taller_id: filterTaller } : {}),
        talleresAPI.getAll({ limit: 500 })  // Aumentar límite para mostrar todos los talleres
      ]);
      // Manejar respuesta paginada o array directo
      const usuariosData = usuariosRes.data?.data || usuariosRes.data || [];
      const talleresData = talleresRes.data?.data || talleresRes.data || [];
      setUsuarios(Array.isArray(usuariosData) ? usuariosData : []);
      // Ordenar talleres por número ANAC
      setTalleres(sortTalleresByAnac(Array.isArray(talleresData) ? talleresData : []));
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: '',
      password: '',
      role: 'taller_user',
      cargo: 'tecnico_aeronautico',
      taller_id: ''
    });
    setEditMode(false);
    setSelectedUser(null);
  };

  const openNewForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (user) => {
    setFormData({
      email: user.email || '',
      full_name: user.full_name || '',
      password: '', // No mostrar contraseña existente
      role: user.role || 'taller_user',
      cargo: user.cargo || 'tecnico_aeronautico',
      taller_id: user.taller_id || ''
    });
    setSelectedUser(user);
    setEditMode(true);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Roles globales (no pertenecen a un taller específico): no requieren taller_id.
    const ROLES_GLOBALES = ['super_admin', 'admin', 'admin_finanzas'];
    const esRolGlobal = ROLES_GLOBALES.includes(formData.role);

    if (!formData.email) {
      toast.error('Email es obligatorio');
      return;
    }
    if (!esRolGlobal && !formData.taller_id) {
      toast.error('Taller es obligatorio para usuarios de taller');
      return;
    }

    if (!editMode && !formData.password) {
      toast.error('La contraseña es obligatoria para nuevos usuarios');
      return;
    }

    setSaving(true);
    try {
      const data = { ...formData };
      if (!data.password) delete data.password; // No enviar password vacío en edición

      if (editMode && selectedUser) {
        await usuariosAPI.update(selectedUser.id, data);
        toast.success('Usuario actualizado correctamente');
      } else {
        await usuariosAPI.create(data);
        toast.success('Usuario creado correctamente');
      }

      setShowForm(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al guardar usuario'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!confirm(`¿Eliminar al usuario ${user.full_name || user.email}?`)) return;
    
    try {
      await usuariosAPI.delete(user.id);
      toast.success('Usuario eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  const filteredUsuarios = usuarios.filter(u =>
    (u.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (u.full_name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (user) => {
    const roleLabels = {
      'super_admin': { label: 'Super Admin', color: 'bg-red-500' },
      'admin': { label: 'Admin', color: 'bg-purple-500' },
      'admin_finanzas': { label: 'Admin Finanzas', color: 'bg-emerald-500' },
      'taller_owner': { label: 'Titular', color: 'bg-amber-500' },
      'taller_user': { label: 'Usuario', color: 'bg-sky-500' },
    };
    
    const cargoLabels = {
      'dueño': 'Dueño',
      'rt': 'RT',
      'inspector': 'Inspector',
      'tecnico_aeronautico': 'Técnico Aeronáutico',
      'mecanico_aeronautico': 'Mecánico Aeronáutico',
      'operario_idoneo': 'Operario Idóneo',
      'secretaria': 'Secretaria/o',
      'administrador': 'Administrador',
    };
    
    const role = user.role || user;
    const cargo = user.cargo;
    const cargoLabel = cargo ? cargoLabels[cargo] : null;
    
    // Si es titular, mostrar "Titular (cargo)" o solo "Titular"
    if (role === 'taller_owner') {
      const label = cargoLabel ? `Titular (${cargoLabel})` : 'Titular';
      return <Badge className="bg-amber-500">{label}</Badge>;
    }
    
    // Si es usuario de taller con cargo
    if (role === 'taller_user' && cargoLabel) {
      return <Badge className="bg-sky-500">{cargoLabel}</Badge>;
    }
    
    const roleInfo = roleLabels[role] || { label: role || 'Usuario', color: 'bg-gray-500' };
    return <Badge className={roleInfo.color}>{roleInfo.label}</Badge>;
  };

  const getTallerName = (tallerId) => {
    const taller = talleres.find(t => t.id === tallerId);
    return taller ? taller.razon_social : '-';
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-6 px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Gestión de Usuarios</h1>
          <Button onClick={openNewForm} className="bg-teal-500 hover:bg-teal-600">
            <Plus className="mr-2 h-4 w-4" /> Nuevo Usuario
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterTaller} onValueChange={setFilterTaller}>
                <SelectTrigger>
                  <SelectValue placeholder="Filtrar por taller" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los talleres</SelectItem>
                  {talleres.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.razon_social}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => { setFilterTaller(''); setSearchTerm(''); }}>
                Limpiar filtros
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Users List */}
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-teal-500" />
              </div>
            ) : filteredUsuarios.length === 0 ? (
              <div className="text-center py-8">
                <User className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-4">No hay usuarios registrados</p>
                <Button onClick={openNewForm} className="bg-teal-500 hover:bg-teal-600">
                  <Plus className="mr-2 h-4 w-4" /> Crear primer usuario
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Usuario</th>
                      <th className="text-left py-3 px-4">Email</th>
                      <th className="text-left py-3 px-4">Taller</th>
                      <th className="text-left py-3 px-4">Rol</th>
                      <th className="text-left py-3 px-4">Estado</th>
                      <th className="text-right py-3 px-4">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsuarios.map(user => (
                      <tr key={user.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
                              <User className="h-5 w-5 text-teal-600" />
                            </div>
                            <div>
                              <p className="font-medium">{user.full_name || 'Sin nombre'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">{user.email}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            <span className="text-sm">{getTallerName(user.taller_id)}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">{getRoleBadge(user)}</td>
                        <td className="py-3 px-4">
                          <Badge variant={user.is_active !== false ? 'default' : 'secondary'}>
                            {user.is_active !== false ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditForm(user)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            {user.role !== 'super_admin' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDelete(user)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Form Dialog */}
        <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) resetForm(); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Taller: solo visible para roles de taller (taller_owner / taller_user). Roles globales no necesitan taller. */}
              {!['super_admin', 'admin', 'admin_finanzas'].includes(formData.role) && (
                <div className="space-y-2">
                  <Label>Taller *</Label>
                  <Select 
                    value={formData.taller_id} 
                    onValueChange={(value) => setFormData({...formData, taller_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar taller..." />
                    </SelectTrigger>
                    <SelectContent>
                      {talleres.map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.razon_social} ({t.numero_anac})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="usuario@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_name">Nombre completo *</Label>
                <Input
                  id="full_name"
                  placeholder="Juan Carlos Pérez"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  Contraseña {editMode ? '(dejar vacío para mantener)' : '*'}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required={!editMode}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tipo de Usuario *</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => {
                    // Al cambiar a rol global, limpiar taller_id (no aplica).
                    const ROLES_GLOBALES = ['super_admin', 'admin', 'admin_finanzas'];
                    if (ROLES_GLOBALES.includes(value)) {
                      setFormData({...formData, role: value, taller_id: ''});
                    } else {
                      setFormData({...formData, role: value});
                    }
                  }}
                  disabled={editMode && selectedUser?.role === 'super_admin'}
                >
                  <SelectTrigger data-testid="select-tipo-usuario">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Solo mostrar super_admin si el usuario ya es super_admin */}
                    {editMode && selectedUser?.role === 'super_admin' && (
                      <SelectItem value="super_admin">Super Administrador</SelectItem>
                    )}
                    <SelectItem value="admin">Administrador (acceso completo)</SelectItem>
                    <SelectItem value="admin_finanzas">Admin Finanzas + Gestoría</SelectItem>
                    <SelectItem value="taller_owner">Titular (administra la cuenta)</SelectItem>
                    <SelectItem value="taller_user">Usuario (acceso normal)</SelectItem>
                  </SelectContent>
                </Select>
                {editMode && selectedUser?.role === 'super_admin' && (
                  <p className="text-xs text-amber-600 mt-1">El rol de Super Admin no se puede cambiar</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Cargo en la Empresa *</Label>
                <Select 
                  value={formData.cargo} 
                  onValueChange={(value) => setFormData({...formData, cargo: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dueño">Dueño</SelectItem>
                    <SelectItem value="rt">RT (Responsable Técnico)</SelectItem>
                    <SelectItem value="inspector">Inspector</SelectItem>
                    <SelectItem value="tecnico_aeronautico">Técnico Aeronáutico</SelectItem>
                    <SelectItem value="mecanico_aeronautico">Mecánico Aeronáutico</SelectItem>
                    <SelectItem value="operario_idoneo">Operario Idóneo</SelectItem>
                    <SelectItem value="secretaria">Secretaria/o</SelectItem>
                    <SelectItem value="administrador">Administrador/Encargado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-teal-500 hover:bg-teal-600" disabled={saving}>
                  {saving ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</>
                  ) : (
                    editMode ? 'Guardar cambios' : 'Crear usuario'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminUsuarios;
